import { debounce } from 'lodash-es';
import { nextTick, ref, watch, type Ref } from 'vue';

interface Particle {
  id: number;
  dx: number;
  dy: number;
  rotation: number;
  delay: number;
  duration: number;
  scale: number;
}

const PARTICLES_DELAY_MS = 300;
const PARTICLES_CLEANUP_DELAY = 1200;

const PARTICLES_COUNT = 6;

export const PARTICLES_ORIGIN_LEFT = '50%';
export const PARTICLES_ORIGIN_TOP = '30%';
export const PARTICLES_TRANSFORM = 'translate(-50%, -50%)';

const PARTICLES_MIN_ANGLE = 210;
const PARTICLES_MAX_ANGLE = 330;
const PARTICLES_BASE_DISTANCE = 40;
const PARTICLES_DISTANCE_VARIATION = 20;

const PARTICLES_MAX_DELAY = 100;
const PARTICLES_BASE_DURATION = 800;
const PARTICLES_DURATION_VARIATION = 400;
const PARTICLES_BASE_SCALE = 0.8;
const PARTICLES_SCALE_VARIATION = 0.4;

export function useFavoriteAnimation(favoriteState: Ref<boolean>) {
  const particles = ref<Particle[]>([]);
  let particleId = 0;

  const generateParticles = () => {
    const newParticles: Particle[] = [];

    for (let i = 0; i < PARTICLES_COUNT; i++) {
      const angle =
        PARTICLES_MIN_ANGLE +
        (PARTICLES_MAX_ANGLE - PARTICLES_MIN_ANGLE) *
          (i / (PARTICLES_COUNT - 1));

      const distance =
        PARTICLES_BASE_DISTANCE + Math.random() * PARTICLES_DISTANCE_VARIATION;
      const dx = Math.cos((angle * Math.PI) / 180) * distance;
      const dy = Math.sin((angle * Math.PI) / 180) * distance;

      newParticles.push({
        id: particleId++,
        dx,
        dy,
        rotation: Math.random() * 360,
        delay: Math.random() * PARTICLES_MAX_DELAY,
        duration:
          PARTICLES_BASE_DURATION +
          Math.random() * PARTICLES_DURATION_VARIATION,
        scale: PARTICLES_BASE_SCALE + Math.random() * PARTICLES_SCALE_VARIATION,
      });
    }

    return newParticles;
  };

  const triggerHeartAnimation = async () => {
    particles.value = generateParticles();

    await nextTick();

    setTimeout(() => {
      particles.value = [];
    }, PARTICLES_CLEANUP_DELAY);
  };

  const debouncedTriggerAnimation = debounce(() => {
    triggerHeartAnimation();
  }, PARTICLES_DELAY_MS);

  /**
   * Triggers animation when favorited, cancels pending animations when unfavorited.
   */
  watch(
    () => favoriteState.value,
    (newValue, oldValue) => {
      if (newValue && newValue !== oldValue) {
        debouncedTriggerAnimation();
      } else if (!newValue) {
        debouncedTriggerAnimation.cancel();
      }
    },
  );

  return {
    particles,
    debouncedTriggerAnimation,
  };
}
